import { useContext } from 'react';
import { StyleSheet } from 'react-native';
import { MBButton, MBClearButton } from '@shared/components/MBButton';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1Bold, MBTextBody2 } from '@shared/components/typography';
import { Subform } from '@shared/data/entity/fields/SubformField';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { ColorThemeContext, SharedUnvalidatedStyles, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { countCompletedSubformFields } from './hooks';

interface Props {
  subform: Subform;
  onSelect: (workflowTemplateId: string) => void;
  onClear: VoidFunction;
  isReadOnly?: boolean;
}

export function SubformContent({ subform, onSelect, onClear, isReadOnly }: Props) {
  const responsiveForms = useResponsiveFormContext();
  const colors = useContext(ColorThemeContext);
  const { workflowTemplate } = useWorkflowTemplate(subform.workflowTemplateId);
  const completedFields = countCompletedSubformFields(
    workflowTemplate?.components ?? [],
    subform.data,
  );

  return (
    <View fill>
      <MBTouchableOpacity onPress={() => onSelect(subform.workflowTemplateId)}>
        {!isReadOnly && (
          <MBView style={styles.clearButton}>
            <MBClearButton onPress={onClear} />
          </MBView>
        )}
        <MBView
          flex
          style={[
            !responsiveForms && { backgroundColor: colors.background },
            responsiveForms ? styles.responsiveCardContainer : styles.cardContainer,
          ]}
        >
          <View>
            <MBTextBody1Bold>{workflowTemplate?.name}</MBTextBody1Bold>
            <View paddingTop={responsiveForms ? 0.5 : 1}>
              <MBTextBody2>
                {i18n('card.blocks.subformsInput.fieldsCompleted', { count: completedFields })}
              </MBTextBody2>
            </View>
          </View>
          {!isReadOnly && (
            <View
              paddingTop={responsiveForms ? 1.5 : 2}
              row={responsiveForms}
              marginLeft={responsiveForms ? -0.5 : 0}
            >
              <MBButton
                title={i18n('card.blocks.subformsInput.start')}
                onPress={() => onSelect(subform.workflowTemplateId)}
                style={{ paddingHorizontal: responsiveForms ? grid(3) : undefined }}
                size={responsiveForms ? 'small' : 'normal'}
              />
            </View>
          )}
        </MBView>
      </MBTouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  cardContainer: {
    ...SharedUnvalidatedStyles.shadow,
    borderRadius: grid(1),
    paddingTop: grid(1.5),
    paddingBottom: grid(1.5),
    paddingHorizontal: grid(2),
  },
  responsiveCardContainer: {
    margin: -1, // cheat into box border so padded content aligns with field label
    paddingTop: grid(1.5),
    paddingBottom: grid(1.5),
    paddingHorizontal: grid(2),
  },
  clearButton: {
    position: 'absolute',
    top: grid(0.5),
    right: grid(0.5),
    zIndex: 1,
  },
});
